import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { resources } from '../../core/services/resources';
import type { DocumentVisualization } from '../../core/types/data-models';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

/** Lazily fetches a single visualization's base64 image and returns a data URI. */
export function useVizImage(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ['document-visualization', id],
    queryFn: () => resources.documentVisualizations.get(id),
    enabled,
    staleTime: Infinity,
  });
}

function Thumb({
  viz,
  selected,
  onClick,
}: {
  viz: DocumentVisualization;
  selected: boolean;
  onClick: () => void;
}) {
  const { data } = useVizImage(viz.id, true);
  const src = data?.imageData ? `data:${data.mimeType};base64,${data.imageData}` : undefined;

  return (
    <Tooltip title={viz.isDefault ? `${formatDate(viz.createdAt)} — default` : formatDate(viz.createdAt)}>
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          width: 64,
          height: 64,
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
          cursor: 'pointer',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {src
          ? <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Skeleton variant="rectangular" width={64} height={64} />}
        {viz.isDefault && (
          <StarIcon
            aria-label="Default visualization"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: 14,
              color: 'warning.main',
              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))',
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

export function SectionVisualizationsDialog({
  open,
  onClose,
  sectionTitle,
  visualizations,
  docId,
  initialSelectedId,
}: {
  open: boolean;
  onClose: () => void;
  sectionTitle: string;
  visualizations: DocumentVisualization[];
  docId: number;
  /** Visualization to select on open — used when jumping in from a specific pick
   * (e.g. the document-wide visualizations list) instead of the section default. */
  initialSelectedId?: number | null;
}) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open && initialSelectedId != null) {
      setSelectedId(initialSelectedId);
    }
  }, [open, initialSelectedId]);

  const handleZoomIn = () => setZoom((z) => Math.min(+(z + 0.25).toFixed(2), 4));
  const handleZoomOut = () => setZoom((z) => Math.max(+(z - 0.25).toFixed(2), 0.25));
  const handleZoomReset = () => setZoom(1);

  // Newest first (already sorted server-side). The dialog opens on the section's
  // default visualization, falling back to the newest one if none is flagged.
  const ordered = visualizations;
  const currentId = selectedId ?? ordered.find((v) => v.isDefault)?.id ?? ordered[0]?.id ?? null;
  const current = ordered.find((v) => v.id === currentId) ?? null;

  const { data: currentFull, isLoading: imageLoading } = useVizImage(currentId ?? 0, open && currentId != null);
  const src = currentFull?.imageData ? `data:${currentFull.mimeType};base64,${currentFull.imageData}` : undefined;

  const handleSetDefault = async () => {
    if (currentId == null) return;
    setSettingDefault(true);
    try {
      await resources.setDefaultVisualization(currentId);
      await queryClient.invalidateQueries({ queryKey: ['document-visualizations', docId] });
    } finally {
      setSettingDefault(false);
    }
  };

  const handleDelete = async () => {
    if (currentId == null) return;
    setDeleting(true);
    try {
      await resources.documentVisualizations.remove(currentId);
      await queryClient.invalidateQueries({ queryKey: ['document-visualizations', docId] });
      setDeleteConfirmOpen(false);
      setSelectedId(null);
      if (ordered.length <= 1) onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Visualizations — {sectionTitle}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {ordered.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No visualizations yet.</Typography>
          ) : (
            <Stack spacing={2}>
              {/* Thumbnail strip (only when more than one) */}
              {ordered.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                  {ordered.map((v) => (
                    <Thumb
                      key={v.id}
                      viz={v}
                      selected={v.id === currentId}
                      onClick={() => setSelectedId(v.id)}
                    />
                  ))}
                </Stack>
              )}

              {/* Center image */}
              <Box>
                {/* Zoom controls */}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                  <Tooltip title="Zoom in">
                    <span>
                      <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 4}>
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Zoom out">
                    <span>
                      <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.25}>
                        <ZoomOutIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Fit to window">
                    <span>
                      <IconButton size="small" onClick={handleZoomReset} disabled={zoom === 1}>
                        <ZoomOutMapIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    {Math.round(zoom * 100)}%
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    overflow: 'auto',
                    display: 'flex',
                    alignItems: zoom <= 1 ? 'center' : 'flex-start',
                    justifyContent: zoom <= 1 ? 'center' : 'flex-start',
                    minHeight: 480,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {imageLoading || !src
                    ? <CircularProgress />
                    : (
                      <Box
                        component="img"
                        src={src}
                        alt={`Visualization of ${sectionTitle}`}
                        sx={{
                          display: 'block',
                          width: zoom !== 1 ? `${zoom * 100}%` : 'auto',
                          maxWidth: zoom === 1 ? '100%' : 'none',
                          maxHeight: zoom === 1 ? 640 : 'none',
                          objectFit: 'contain',
                          transition: 'width 0.15s, max-width 0.15s',
                        }}
                      />
                    )}
                </Box>
              </Box>

              {/* Metadata */}
              {current && (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Generated {formatDate(current.createdAt)}
                    {current.model ? ` · ${current.model}` : ''}
                    {current.isDefault ? ' · default visualization' : ''}
                  </Typography>
                  {current.userInstructions && (
                    <Typography variant="caption" color="text.secondary">
                      Instructions: {current.userInstructions}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {current && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {/* With a single visualization there is nothing to choose — it is
              already the section's default. */}
          {current && ordered.length > 1 && (
            <Button
              startIcon={current.isDefault ? <StarIcon /> : <StarBorderIcon />}
              onClick={handleSetDefault}
              disabled={current.isDefault || settingDefault}
            >
              {current.isDefault ? 'Default' : 'Set as default'}
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete visualization?</DialogTitle>
        <DialogContent>
          <DialogContentText>This visualization will be permanently deleted.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" size="small" disabled={deleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
